import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { usePasskeys } from '../hooks/usePasskeys';
import AppLayout from '../components/layout/AppLayout';
import Card from '../components/common/Card';
import TOTPSetupModal from '../components/settings/TOTPSetupModal';
import TOTPDisableModal from '../components/settings/TOTPDisableModal';
import PasskeyModal from '../components/settings/PasskeyModal';
import ChangePasswordModal from '../components/settings/ChangePasswordModal';
import {
  IMPERSONATION_PASSWORD_REASON,
  IMPERSONATION_PASSKEYS_REASON,
} from '../components/settings/impersonationReasons';
import { useImpersonation, getImpersonationTarget } from '../hooks/useImpersonation';
import InlineEditField from '../components/settings/InlineEditField';
import LoginEmailSection from '../components/settings/LoginEmailSection';
import { updateProfile } from '../api/auth';
import { updateOrganization } from '../api/organization';

export default function SettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { hasPasskeys, passkeyCount, refetch: refetchPasskeys } = usePasskeys();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Password and passkey changes go to Hanko with the logged-in session, so while an
  // SA impersonates they would change the SA's own account. Same check as AppLayout's
  // canDismissNotices (sessionStorage too: the hook's state is per instance).
  const { isImpersonating } = useImpersonation();
  const canManageSignIn = !isImpersonating && !getImpersonationTarget();

  // Every role can edit their own name.
  const updateNameMutation = useMutation({
    mutationFn: (newName: string) => updateProfile({ name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // Organization name editing is admin/sa only (also enforced server-side).
  const canEditOrg = user?.role === 'admin' || user?.role === 'sa';
  const updateOrgMutation = useMutation({
    mutationFn: (newName: string) => updateOrganization({ name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  const totpEnabled = user?.totp_enabled ?? false;

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <div className="space-y-6 max-w-2xl">
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Account Information
          </h2>
          <div className="space-y-4">
            {/* Name Field */}
            <InlineEditField
              label="Name"
              value={user?.name ?? null}
              canEdit
              onSave={(trimmed) => updateNameMutation.mutateAsync(trimmed)}
              isPending={updateNameMutation.isPending}
              serverError={updateNameMutation.error?.message ?? null}
              placeholder="Enter your name"
              inputId="name-input"
            />

            {/* Organization Field */}
            <InlineEditField
              label="Organization"
              value={user?.organization_name ?? null}
              canEdit={canEditOrg}
              onSave={(trimmed) => updateOrgMutation.mutateAsync(trimmed)}
              isPending={updateOrgMutation.isPending}
              serverError={updateOrgMutation.error?.message ?? null}
              placeholder="Enter organization name"
              inputId="org-name-input"
              editButtonLabel="Edit organization"
            />

            {/* Login email (self-service change) */}
            {user && (
              <LoginEmailSection
                email={user.email}
                role={user.role}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ['user'] })}
              />
            )}

            {/* Role Field */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Role
              </label>
              <p className="text-gray-900 capitalize">{user?.role}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Password
          </h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-gray-700">Change the password you use to sign in.</p>
              {!canManageSignIn && (
                <p className="text-sm text-gray-500 mt-1">{IMPERSONATION_PASSWORD_REASON}</p>
              )}
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              disabled={!canManageSignIn}
              title={canManageSignIn ? undefined : IMPERSONATION_PASSWORD_REASON}
              className="shrink-0 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
            >
              Change password
            </button>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Two-Factor Authentication
          </h2>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-700">
                {totpEnabled ? (
                  <span className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Enabled
                    </span>
                    Two-factor authentication is active
                  </span>
                ) : (
                  <span className="text-gray-600">
                    Add an extra layer of security to your account
                  </span>
                )}
              </p>
              {!totpEnabled && (
                <p className="text-sm text-gray-500 mt-1">
                  Use an authenticator app like Google Authenticator or Authy
                </p>
              )}
            </div>

            {totpEnabled ? (
              <button
                onClick={() => setShowDisableModal(true)}
                className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md font-medium transition-colors"
              >
                Disable
              </button>
            ) : (
              <button
                onClick={() => setShowSetupModal(true)}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md font-medium transition-colors"
              >
                Enable 2FA
              </button>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Passkeys
          </h2>
          <div className="flex items-center justify-between gap-4">
            {!canManageSignIn ? (
              // The passkey list would be the SA's own, so don't show it.
              <p className="text-sm text-gray-500">{IMPERSONATION_PASSKEYS_REASON}</p>
            ) : (
              <div>
                <p className="text-gray-700">
                  {hasPasskeys ? (
                    <span className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {passkeyCount} {passkeyCount === 1 ? 'Passkey' : 'Passkeys'}
                      </span>
                      Passwordless sign-in is enabled
                    </span>
                  ) : (
                    <span className="text-gray-600">
                      Enable passwordless sign-in with passkeys
                    </span>
                  )}
                </p>
                {!hasPasskeys && (
                  <p className="text-sm text-gray-500 mt-1">
                    Use your device's biometrics or security key
                  </p>
                )}
              </div>
            )}
            <button
              onClick={() => setShowPasskeyModal(true)}
              disabled={!canManageSignIn}
              title={canManageSignIn ? undefined : IMPERSONATION_PASSKEYS_REASON}
              className={`shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                hasPasskeys || !canManageSignIn
                  ? 'px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium'
                  : 'px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md font-medium transition-colors'
              }`}
            >
              {/* Neutral while impersonating: the count is the SA's own. */}
              {hasPasskeys || !canManageSignIn ? 'Manage Passkeys' : 'Enable Passkey'}
            </button>
          </div>
        </Card>
      </div>

      {/* Modals */}
      <TOTPSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSuccess={() => setShowSetupModal(false)}
      />
      <TOTPDisableModal
        isOpen={showDisableModal}
        onClose={() => setShowDisableModal(false)}
        onSuccess={() => setShowDisableModal(false)}
      />
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
      <PasskeyModal
        isOpen={showPasskeyModal}
        onClose={() => {
          setShowPasskeyModal(false);
          refetchPasskeys();
        }}
      />
    </AppLayout>
  );
}
